import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { TileLayer } from "react-leaflet";
import { connect } from "react-redux";

import FlowUtils from "services/flow/utils";
import LiveActioner from "services/live/actioner";
import LiveUtils from "services/live/utils";
import MapActioner from "services/map/actioner";
import MapConfig from "services/map/MapConfig";
import MapUtils from "services/map/utils";
import PedestrianStatisticsUtils from "services/pedestrianStatistics/utils";
import Abilities from "services/permissions/abilities";
import ParkkiTheme from "themes/ParkkiTheme";

import AreasLayers from "components/AreasLayers";
import AvailableParkingLayers from "components/AvailableParkingLayers";
import DisplaysLayers from "components/DisplaysLayers";
import LeafletMap from "components/LeafletMap";
import ParkedCarsLayers from "components/ParkedCarsLayers";
import PathsLayers from "components/PathsLayers";
import PointsLayers from "components/PointsLayers";

function MapComponent({
  isLive,
  availableParking,
  customMarkers,
  pedestrianDetails,
  parkDetails,
  parkedCars,
  currentFilters,
  liveDetails,
  liveGeneral,
  liveMap,
  mapConfig,
  center,
  initFloorsData,
  maxZoom,
  minZoom,
  onRecenter,
  retrieveLayersInfos,
  setConfigParam,
  tilesUrl,
  zoom,
  onSelectLayers,
  pedestrianStatistics,
}) {
  const intl = useIntl();
  const [leafletMap, setLeafletMap] = useState();

  useEffect(() => {
    async function initializeData() {
      if (liveMap?.initialized) {
        await initFloorsData();
        return null;
      }

      await initFloorsData();
      await retrieveLayersInfos();
    }

    setTimeout(initializeData, 200);
  }, [leafletMap, liveMap?.initialized, initFloorsData, retrieveLayersInfos]);

  useEffect(() => {
    if (liveMap?.initialized && leafletMap) {
      onRecenter(leafletMap?.leafletElement);
    }
  }, [liveMap?.initialized, leafletMap]);

  const formattedFilters = useMemo(() => {
    return {
      ...LiveUtils.getFromToFiltersFromTimestamp(currentFilters?.timestamp),
      ...currentFilters,
    };
  }, [currentFilters]);

  const formattedOptions = useMemo(() => {
    const flowItems = (liveDetails?.data || [])?.reduce((previous, current) => {
      return [...previous, ...(current?.items || [])];
    }, []);
    const parkItems = parkDetails?.data?.items || [];
    const pedestrianItems =
      pedestrianDetails?.data
        ?.map((details) => {
          // This code is a workaround to handle the different structure of the pedestrianDetails data (like queue)
          const newObj = { ...details };
          newObj.areas = details.data || details.areas;
          delete newObj.data;
          return newObj;
        })
        .map((item) => item.items)
        .flat() || [];

    return {
      items: [...flowItems, ...parkItems, ...pedestrianItems],
    };
  }, [pedestrianDetails, parkDetails, liveDetails]);

  const selectedAreasId = useMemo(() => {
    if (!liveMap?.initialized) {
      return [];
    }

    return (
      liveMap?.selectedLayers
        ?.filter((item) => item?.type === "area")
        .map((item) => item.area_id) || []
    );
  }, [liveMap?.initialized, liveMap?.selectedLayers]);

  const selectedParkedCars = useMemo(() => {
    if (!liveMap?.initialized || !parkedCars?.initialized) {
      return { initialized: false, items: [], length: 0 };
    }

    const selectedCars = parkedCars?.items?.filter(
      (parkedCar) =>
        selectedAreasId?.indexOf(parkedCar?.meta_data?.area?.id) >= 0
    );

    return {
      ...parkedCars,
      items: selectedCars || [],
      length: selectedCars?.length || 0,
    };
  }, [parkedCars, liveMap?.initialized, selectedAreasId]);

  const selectedAvailableParking = useMemo(() => {
    if (!liveMap?.initialized || !availableParking?.initialized) {
      return { initialized: false, items: [], length: 0 };
    }

    const selectedParking = availableParking?.items?.filter(
      (item) => selectedAreasId?.indexOf(item?.meta_data?.area?.id) >= 0
    );

    return {
      ...parkedCars,
      items: selectedParking || [],
      length: selectedParking?.length || 0,
    };
  }, [availableParking, liveMap?.initialized, selectedAreasId]);

  function getMapRadius(currentMap) {
    return MapUtils.getRadiusInMeters(
      currentMap.getBounds().getNorthEast(),
      currentMap.getCenter()
    );
  }

  function handleClosePopup() {
    return leafletMap?.leafletElement?.closePopup();
  }

  function handleZoomEnd(e) {
    const currentMap = e?.target;

    handleClosePopup();
    setConfigParam("currentRadius", getMapRadius(currentMap));
    setConfigParam("currentZoom", currentMap.getZoom());
  }

  function handleMoveEnd(e) {
    const currentMap = e?.target;

    handleClosePopup();
    setConfigParam("currentRadius", getMapRadius(currentMap));
    setConfigParam("currentPosition", currentMap.getCenter());
  }

  function handleOnLoad(e) {
    const currentMap = e?.target;
    setConfigParam("currentRadius", getMapRadius(currentMap._map));
    setConfigParam("currentPosition", currentMap._map.getCenter());
  }

  function handleIsLabelDisplayed(layer) {
    if (layer.apiType === "VIRTUAL") {
      return true;
    }
    return layer?.selected;
  }

  function handleHasLabelBackground(layer) {
    const type = layer?.type;
    const apiType = layer?.apiType;
    const layerOptions = layer?.options;

    if (apiType === "VIRTUAL") {
      return false;
    }

    if (
      type === "point" &&
      !layerOptions?.background?.out &&
      !layerOptions?.background?.in
    ) {
      return false;
    }

    if (
      type === "area" &&
      apiType === "FLOW" &&
      !layerOptions?.background?.out &&
      !layerOptions?.background?.in
    ) {
      return false;
    }

    return layer?.selected;
  }

  function handleGetLabelLength(layer) {
    const computedValue = layer?.meta_data?.computed_value || "";
    let labelWidth = computedValue.length;
    let nbSymbols = 0;

    if (layer?.meta_data?.has_symbol) {
      const symbolConfig = layer?.meta_data?.symbol_config || {};
      // We remove the symbol characters from the label to calculate the correct length and we count the number of symbols to add them with the correct width after
      labelWidth = Object.keys(symbolConfig).reduce((previous, currentKey) => {
        if (previous.includes(currentKey)) {
          nbSymbols += 1;
        }

        return previous.replace(currentKey, "");
      }, computedValue).length;

      labelWidth += nbSymbols;
    }

    // Some stramatel displays have a pictogram as first character, we remove it
    if (layer?.meta_data?.has_pictogram && layer.apiType === "STRAMATEL") {
      labelWidth -= 1;
    }

    return {
      length: labelWidth,
      nbSymbols,
    };
  }

  function handleGetScrolling(layer) {
    if (!layer?.meta_data?.scrolling || !layer?.meta_data?.max_digits) {
      return false;
    }

    const labelWidth = handleGetLabelLength(layer).length;

    return labelWidth > layer?.meta_data?.max_digits;
  }

  function handleGetAlternating(layer) {
    if (
      !layer?.meta_data?.message_delimiters ||
      !layer?.meta_data?.computed_value
    ) {
      return null;
    }

    const delimiters = layer?.meta_data?.message_delimiters;
    const computedValue = layer?.meta_data?.computed_value;

    const foundDelimiter = delimiters.find((delimiter) =>
      computedValue.includes(delimiter)
    );

    if (!foundDelimiter) {
      return null;
    }

    const messages = computedValue
      .split(foundDelimiter)
      .filter((msg) => msg.trim() !== "");

    if (messages.length <= 1) {
      return null;
    }

    if (!layer?.meta_data?.has_symbol) {
      return messages;
    }

    const symbolConfig = layer?.meta_data?.symbol_config || {};
    return messages.map((message) =>
      getDisplayLabelWithSymbols(message, symbolConfig)
    );
  }

  function getDisplayLabelWithSymbols(computedValue = "", symbolConfig = {}) {
    const tokenKeys = Object.keys(symbolConfig);

    if (!tokenKeys.length || typeof computedValue !== "string") {
      return computedValue;
    }

    // Symbol tokens (e.g. "[FG]") include regex control characters like "[" and "]".
    // Escaping them forces literal matching, so we only match real token strings.
    const escapedTokens = tokenKeys.map((token) =>
      token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    );
    // Capturing group + global flag lets split() keep matched tokens in output.
    const tokenRegex = new RegExp(`(${escapedTokens.join("|")})`, "g");

    const parsedLabel = computedValue
      .split(tokenRegex)
      .filter((part) => part !== "")
      .map((part, index) => {
        const symbolName = symbolConfig?.[part];
        const CurrentSymbol = MapConfig?.iot?.display?.symbol?.[symbolName];

        if (!CurrentSymbol) {
          return part;
        }

        return <CurrentSymbol key={`${part}-${index}`} />;
      });

    if (parsedLabel.every((part) => typeof part === "string")) {
      return parsedLabel.join("");
    }

    return parsedLabel;
  }

  function handleGetLayerLabel(layer) {
    const areaName = layer?.short_name || layer?.long_name || null;

    if (layer.apiType === "QUEUEMANAGEMENT") {
      const foundArea = pedestrianDetails?.data
        ?.map((d) => {
          if (d.data) {
            return d.data.find((a) => a.area === layer.id);
          }

          return null;
        })
        .filter((area) => area !== null)[0];

      if (!foundArea) {
        return areaName;
      }

      const condition =
        PedestrianStatisticsUtils.getBackofficeSettingsConditionWithValue(
          pedestrianStatistics?.config?.queue_management,
          foundArea.area,
          foundArea.waiting_time_in_seconds / 60
        );

      if (!condition) {
        return areaName;
      }

      return `${condition?.symbol} ${condition?.value} min`;
    }

    if (
      layer.type === "point" ||
      layer.type === "area" ||
      (layer.type === "path" && layer.apiType === "VIRTUAL")
    ) {
      return layer?.short_name || layer?.long_name || null;
    }

    if (layer.type === "display") {
      if (layer.status === "STAND_BY") {
        return "";
      }
      if (layer?.meta_data?.has_symbol) {
        return getDisplayLabelWithSymbols(
          layer?.meta_data?.computed_value || "",
          layer?.meta_data?.symbol_config || {}
        );
      }
      let value = layer?.meta_data?.computed_value;
      // Some stramatel displays have a pictogram as first character, we remove it
      if (layer?.meta_data?.has_pictogram && layer.apiType === "STRAMATEL") {
        value = value.substring(1, value.length);
      }
      return value;
    }

    const generalStatsForLayer = liveGeneral?.data?.find((currentStats) => {
      if (layer?.id === currentStats?.path_id) {
        return currentStats;
      }

      return null;
    });

    if (layer?.apiType === "TRAFFIC") {
      return intl.messages[
        `flowScene.traffic.${
          generalStatsForLayer?.status?.toLowerCase() ?? "fault"
        }`
      ];
    }

    if (
      generalStatsForLayer?.travel_time_median === null ||
      generalStatsForLayer?.travel_time_median === undefined
    ) {
      return null;
    }

    let travelTimeStr = null;

    if (
      generalStatsForLayer.duration_unit != undefined &&
      generalStatsForLayer.duration_unit == "SECONDS"
    ) {
      const minutes = Math.floor(generalStatsForLayer?.travel_time_median / 60);
      const secondsLeft = generalStatsForLayer?.travel_time_median % 60;

      if (minutes === 0) {
        travelTimeStr = `${secondsLeft}${intl.messages["components.timeSlider.second"]}`;
      } else {
        let secondsLeftStr = secondsLeft.toString();
        if (secondsLeft < 10) {
          secondsLeftStr = `0${secondsLeft}`;
        }
        if (secondsLeft === 0) {
          travelTimeStr = `${minutes}${intl.messages["components.timeSlider.mins"]}`;
        } else {
          travelTimeStr = `${minutes}${intl.messages["components.timeSlider.mins"]}${secondsLeftStr}${intl.messages["components.timeSlider.second"]}`;
        }
      }
    } else if (
      generalStatsForLayer.duration_unit != undefined &&
      generalStatsForLayer.duration_unit == "MINUTES"
    ) {
      if (generalStatsForLayer?.travel_time_median === 0) {
        travelTimeStr = `< 1${intl.messages["components.timeSlider.mins"]}`;
      } else {
        travelTimeStr = `${generalStatsForLayer?.travel_time_median}${intl.messages["components.timeSlider.mins"]}`;
      }
    }
    return travelTimeStr;
  }

  function handleGetLayerBackground(layer) {
    const generalStatsForLayer = liveGeneral?.data?.find((currentStats) => {
      if (layer?.id === currentStats?.path_id) {
        return currentStats;
      }

      return null;
    });

    if (generalStatsForLayer?.apiType === "TRAFFIC") {
      if (generalStatsForLayer?.status === "FLUID") {
        return {
          config: {
            mainColor: ParkkiTheme.colors.green,
          },
        };
      }

      if (generalStatsForLayer?.status === "LOADED") {
        return {
          config: {
            mainColor: ParkkiTheme.colors.orange,
          },
        };
      }

      if (generalStatsForLayer?.status === "SATURATED") {
        return {
          config: {
            mainColor: ParkkiTheme.colors.red,
          },
        };
      }

      if (generalStatsForLayer?.status === "FAULT") {
        return {
          config: {
            mainColor: ParkkiTheme.colors.disabledGrey,
          },
        };
      }
    }

    const travelTimePrevisional = generalStatsForLayer?.travel_time_previsional;
    const travelTimeMedian = Math.round(
      generalStatsForLayer?.travel_time_median
    );

    let backgroundColor = ParkkiTheme.colors.white;
    if (travelTimeMedian <= travelTimePrevisional?.median * 1.1) {
      // add 10% margin for normal conditions
      backgroundColor = ParkkiTheme.colors.green;
    } else if (
      travelTimeMedian > travelTimePrevisional?.median &&
      travelTimeMedian <= travelTimePrevisional?.quartile_75
    ) {
      backgroundColor = ParkkiTheme.colors.orange;
    } else if (travelTimeMedian > travelTimePrevisional?.quartile_75) {
      backgroundColor = ParkkiTheme.colors.flatRed;
    }

    if (
      travelTimeMedian === null ||
      travelTimePrevisional?.median === null ||
      travelTimePrevisional?.median === null ||
      travelTimePrevisional?.median === null
    ) {
      backgroundColor = ParkkiTheme.colors.white;
    }

    return {
      out: 0,
      config: {
        mainColor: ParkkiTheme.colors.green,
        valueColors: {
          91: {
            backgroundColor: backgroundColor,
          },
        },
      },
    };
  }

  function handleGetLayerLabelIcon(layer) {
    if (layer?.options?.arrow_icon) {
      return FlowUtils.getPointIcon(layer?.options?.arrow_icon);
    }

    return MapConfig?.iconByType?.live?.[layer.type]?.[layer.apiType];
  }

  function handleGetLabelColor(layer) {
    if (layer?.type === "display") {
      const apiColor = layer?.meta_data?.color || "default";
      return MapConfig?.iot?.display?.color?.[apiColor];
    }
    return null;
  }

  return (
    <LeafletMap
      preferCanvas
      style={{
        width: "100%",
        height: "100%",
      }}
      setMapRef={setLeafletMap}
      center={center}
      zoom={zoom}
      minZoom={minZoom}
      maxZoom={maxZoom}
      onZoomEnd={handleZoomEnd}
      onMoveEnd={handleMoveEnd}
    >
      <TileLayer url={MapConfig.defaultTileUrl} />
      <TileLayer
        url={tilesUrl}
        onLoad={handleOnLoad}
        minZoom={minZoom}
        maxZoom={maxZoom}
      />
      <AreasLayers
        map={liveMap}
        options={formattedOptions}
        onSelectLayers={onSelectLayers}
        isLabelDisplayed={handleIsLabelDisplayed}
        hasLabelBackground={handleHasLabelBackground}
        getLabelIcon={handleGetLayerLabelIcon}
        getLabel={handleGetLayerLabel}
        filters={formattedFilters}
      />

      {Abilities.ability.can("read", "tabs.flow") ? (
        <PathsLayers
          loading={!liveDetails?.initialized}
          map={liveMap}
          options={formattedOptions}
          onSelectLayers={onSelectLayers}
          getLabelIcon={handleGetLayerLabelIcon}
          getLabel={handleGetLayerLabel}
          getBackground={handleGetLayerBackground}
          isLabelDisplayed={handleIsLabelDisplayed}
          hasLabelBackground={handleHasLabelBackground}
          filters={formattedFilters}
        />
      ) : null}

      {Abilities.ability.can("read", "tabs.flow") ? (
        <PointsLayers
          loading={!liveDetails?.initialized}
          map={liveMap}
          options={formattedOptions}
          onSelectLayers={onSelectLayers}
          filters={formattedFilters}
          isLabelDisplayed={handleIsLabelDisplayed}
          getLabelIcon={handleGetLayerLabelIcon}
          getLabel={handleGetLayerLabel}
          hasLabelBackground={handleHasLabelBackground}
        />
      ) : null}

      {Abilities.ability.can("read", "tabs.park_stats") ? (
        <ParkedCarsLayers
          cars={selectedParkedCars}
          options={{
            ...customMarkers,
            timestamp: currentFilters?.timestamp,
            zoom,
          }}
        />
      ) : null}

      {Abilities.ability.can("read", "tabs.park_stats") ? (
        <AvailableParkingLayers
          cars={selectedAvailableParking}
          options={{
            ...customMarkers,
            timestamp: currentFilters?.timestamp,
            zoom,
          }}
        />
      ) : null}

      {isLive &&
      Abilities.ability.can("read", "tabs.live") &&
      mapConfig?.displaysDisplayed ? (
        <DisplaysLayers
          map={liveMap}
          getLabelColor={handleGetLabelColor}
          getLabel={handleGetLayerLabel}
          getLabelLength={handleGetLabelLength}
          getScrolling={handleGetScrolling}
          getAlternating={handleGetAlternating}
          filters={formattedFilters}
          disabled={true}
        />
      ) : null}
    </LeafletMap>
  );
}

const mapStateToProps = (state) => ({
  customMarkers: state.Map?.config?.custom_markers,
  center: MapUtils.getDefaultCenter(state.Map),
  contract: state.Contract.contract,
  currentFilters: state.Live.filters,
  maxZoom: MapUtils.getMaxZoom(state.Map),
  minZoom: MapUtils.getMinZoom(state.Map),
  liveMap: state.Live.map,
  liveDetails: state.Live.flowDetails,
  liveGeneral: state.Live.flowGeneral,
  mapConfig: state.Map.config,
  parkDetails: state.Live.parkDetails,
  pedestrianDetails: state.Live.pedestrianDetails,
  tilesUrl: MapUtils.getTilesUrl(state.Map),
  zoom: MapUtils.getZoom(state.Map),
  availableParking: state.Live.availableParking,
  parkedCars: state.Live.parkedCars,
  pedestrianStatistics: state.PedestrianStatistics,
});

const mapDispatchToProps = (dispatch) => ({
  retrieveLayersInfos: () => dispatch(LiveActioner.retrieveLayersInfos()),
  setConfigParam: (key, value) =>
    dispatch(MapActioner.setConfigParam(key, value)),
  initFloorsData: () => dispatch(MapActioner.initFloorsDataFromModule("live")),
});

export default connect(mapStateToProps, mapDispatchToProps)(MapComponent);
